import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, IconButton } from "@material-ui/core";
import { green, red, yellow } from "@material-ui/core/colors";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Checkbox,
  Grid,
  TablePagination
} from "@material-ui/core";

import {
  Visibility,
  Lens as LensIcon,
  VisibilityOff
} from "@material-ui/icons";

import { fetchDcmPlacements } from "../../actions/dcm/placements";
import { fetchAds } from "../../actions/dcm/ads";
import { fetchAssignedCreatives } from "../../actions/dcm/assignedCreatives";
import { getPlacementInfo } from "../../actions/sql/placementInfo";
import {
  resetCreativeState,
  setEndDate,
  setStartDate
} from "../../actions/app/creatives";
import { useDispatch, useSelector } from "react-redux";
import { getTypes } from "../../actions/sql/typeGroups";
import { typeGroupNames } from "../../utils/dataUtils";

import CampaignSelect from "../CampaignSelect";
import EnhancedTableHead from "./EnhancedTableHead";
import EnhancedTableToolbar from "./EnhancedTableToolbar";
import { EnhancedToolbarActions } from "./EnhancedToolbarActions";
import FilterChips from "./FilterChips";
import { DimensionDefaultList } from "./DimensionDefaultList";

import AssignedCreativesDialog from "../AssignedCreatives/AssignedCreativesDialog";
import { searchDataByField } from "../../utils/dataUtils";
import { fetchSitesService } from "../../services/dcm/sites";
import { fetchDcmLandingPages } from "../../actions/dcm/landingPages";
import CreativePreviewDialog from "../AssignedCreatives/CreativePreviewDialog";

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    paddingTop: theme.spacing(3)
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(2, 4, 0),
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  paperModal: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: "50%",
    overflow: "auto",
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  tableWrapper: {
    overflowX: "auto"
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  overdue: {
    color: red[500],
    fontWeight: 500,
    padding: "6px 8px 6px 16px"
  },
  tableCell: {
    padding: "6px 8px 6px 16px"
  },
  container: {
    maxHeight: "60vh"
  },
  pagination: {
    marginLeft: -14
  },
  defaultGrid: { marginTop: "10px", marginBottom: "10px" }
}));

const getSitesByIds = async (placementData) => {
  if (placementData.length === 0) return [];

  let uniqueSiteIds = [];

  placementData.map((placement) => {
    if (!uniqueSiteIds.includes(placement.siteId)) {
      uniqueSiteIds.push(placement.siteId);
    }
    return placement;
  });

  return await fetchSitesService(uniqueSiteIds);
};

const applyFilters = (placements, placementInfo, filters) => {
  // Status filter
  if (filters.status !== "All") {
    placements = placements.filter((item) => {
      const traffickingStatus = placementInfo.byId[item.id].Trafficking_Status;

      switch (filters.status) {
        case "Not submitted":
          return traffickingStatus === "Not Trafficked";
        case "Default creative assigned":
          return traffickingStatus === "Default Ad Assigned";
        case "Submitted":
          return traffickingStatus === "Trafficked";
        default:
          return false;
      }
    });
  }

  // Site filter
  if (filters.site !== "All") {
    placements = placements.filter((item) => {
      return filters.site === item.siteId;
    });
  }

  // Start filter
  let filterStartDates = filters.start.split(',');
  if (!filterStartDates.includes('All')) {
    filterStartDates = filterStartDates.map(d => dayjs(d))

    placements = placements.filter((item) => {
      const info = placementInfo.byId[item.id];
      if (info) {
        return filterStartDates.some((d)=> {
          return dayjs(info.Start_Date).isSame(d);
        });
      }
    });
  }

  // End filter
  let filterEndDates = filters.end.split(',');
  if (!filterEndDates.includes('All')) {
    filterEndDates = filterEndDates.map(d => dayjs(d))

    placements = placements.filter((item) => {
      const info = placementInfo.byId[item.id];
      if (info) {
        return filterEndDates.some((d)=> {
          return dayjs(info.End_Date).isSame(d);
        });
      }
    });
  }

  // Lead time filter
  if (filters.leadTime !== "All") {
    placements = placements.filter((item) => {
      const startDate = new Date(item.pricingSchedule.startDate);
      const now = new Date();
      const diff = startDate - now;
      const oneDay = 1000 * 60 * 60 * 24;
      const day = Math.floor(diff / oneDay);

      return filters.leadTime === day;
    });
  }

  // Last modified filter
  if (filters.lastModified !== "All") {
    placements = placements.filter((item) => {
      return filters.lastModified === item.lastModifiedInfo.time;
    });
  }

  // Dimensions filter
  if (filters.dimensions !== "All") {
    placements = placements.filter(
      (item) =>
        filters.dimensions === `${item.size.width} x ${item.size.height}`
    );
  }

  // Compatibility filter
  if (filters.compatibility !== "All") {
    placements = placements.filter((item) => {
      return filters.compatibility === item.compatibility;
    });
  }

  // Serving types filter
  if (filters.servingType !== "All") {
    placements = placements.filter((item) => {
      const info = placementInfo.byId[item.id];
      const servingType = info.Serving_Type ? info.Serving_Type : "N/A";
      return servingType === filters.servingType;
    });
  }

  return [...placements];
};

export default function PlacementList() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const campaignStoreById = useSelector((state) => state.dcmCampaigns.byId);
  const selectedCampaignId = useSelector(
    (state) => state.dcmCampaigns.selectedId
  );
  const [tableData, setTableData] = useState([]);
  const placements = useSelector((state) => state.dcmPlacements);
  const areCreativesLoaded = useSelector(
    (state) => state.dcmAssignedCreatives.isLoading
  );
  const areTypesLoaded = useSelector(
    (state) => state.typeGroups.allGroupNames.length > 0
  );
  const placementInfo = useSelector((state) => state.placementInfo);
  const rows = [];

  const [filters, setFilters] = useState({
    status: "All",
    site: "All",
    start: "All",
    end: "All",
    leadTime: "All",
    lastModified: "All",
    dimensions: "All",
    compatibility: "All",
    servingType: "All",
  });

  const [creativesModalState, setCreativesModalState] = React.useState({
    isOpen: false,
    selectedId: ""
  });
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("headers");
  const [selectedPlacements, setSelectedPlacements] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [placementSites, setPlacementSites] = React.useState({});
  const [open, setOpen] = React.useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = React.useState(false)
  const [creativePreviewURL, setCreativePreviewURL] = React.useState("");
  const [selectedCreativePreview, setSelectedCreativePreview] = React.useState({})

  const stableDispatch = useCallback((args) => dispatch(args), [dispatch]);

  useEffect(() => {
    if (selectedCampaignId) {
      stableDispatch(fetchDcmPlacements(selectedCampaignId));
      stableDispatch(fetchAds(selectedCampaignId));
      stableDispatch(fetchAssignedCreatives(selectedCampaignId));
      stableDispatch(getPlacementInfo(selectedCampaignId));
      stableDispatch(
        fetchDcmLandingPages([
          campaignStoreById[selectedCampaignId].advertiserId
        ])
      );
      setPage(0); //return to first page on new campaign
    }
  }, [selectedCampaignId, stableDispatch, campaignStoreById]);

  useEffect(() => {
    if (selectedCampaignId) {
      //SET END DATE DEFAULT AS CAMPAIGN END DATE HERE
      let selectedCampaignObj = campaignStoreById[selectedCampaignId];
      stableDispatch(setEndDate(selectedCampaignObj.endDate, false));
    }
  }, [
    selectedCampaignId,
    campaignStoreById,
    stableDispatch
  ]);

  useEffect(() => {
    if (!areTypesLoaded) dispatch(getTypes(typeGroupNames));
  }, [dispatch, areTypesLoaded]);

  useEffect(() => {
    dispatch(resetCreativeState());
  }, [dispatch]);

  useEffect(() => {
    getSitesByIds(placements.data)
      .then((sites) => {
        let sitesById = {};
        sites.map((site) => {
          sitesById[site.id] = site;
          return site;
        });

        setPlacementSites(sitesById);
      })
      .catch((err) => console.log(err));
  }, [placements, setPlacementSites]);

  useEffect(() => {
    setTableData(
      searchDataByField(
        applyFilters(placements.data, placementInfo, filters),
        "name",
        "id",
        searchText
      )
    );
  }, [filters, placementInfo, placements.data, searchText]);

  const determineStatusColor = (placementInfoObj) => {
    let color = red[300];

    if (placementInfoObj) {
      switch (placementInfoObj.Trafficking_Status) {
        case "Not Trafficked":
          color = red[300];
          break;
        case "Default Ad Assigned":
          color = yellow[300];
          break;
        case "Trafficked":
          color = green[300];
          break;
        default:
          color = red[300];
      }
    }

    return (
      <IconButton disabled={true}>
        <LensIcon style={{ color: color }} />
      </IconButton>
    );
  };

  const determineServingType = (placementInfoObj) => {
    if (placementInfoObj) {
      switch (placementInfoObj.Serving_Type) {
        case "3rd party":
          return "3rd Party";
        case "Site":
          return "Site Served";
        default:
          return "N/A";
      }
    } else {
      return "N/A";
    }
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked && selectedPlacements.length === 0) {
      const newSelecteds = tableData;
      setSelectedPlacements(newSelecteds);
      return;
    }
    setSelectedPlacements([]);
  };

  const handleClick = (event, placement) => {
    const selectedIndex = selectedPlacements.indexOf(placement);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedPlacements, placement);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedPlacements.slice(1));
    } else if (selectedIndex === selectedPlacements.length - 1) {
      newSelected = newSelected.concat(selectedPlacements.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedPlacements.slice(0, selectedIndex),
        selectedPlacements.slice(selectedIndex + 1)
      );
    }

    setSelectedPlacements([...new Set(newSelected)]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const isSelected = (row) => selectedPlacements.indexOf(row) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleClickOpen = (id) => {
    setOpen(true);
    setCreativesModalState({ selectedId: id });
  };

  const isAddingCreativeAllowed = () => {
    let dcmSelectedPlacementIds = selectedPlacements.map(
      (dcmPlacement) => dcmPlacement.id
    );

    let sqlSelectedPlacements = Object.values(
      placementInfo.byId
    ).filter((placementInfo) =>
      dcmSelectedPlacementIds.includes(placementInfo.Placement_Id)
    );

    if (selectedPlacements.length === 0) {
      return false;
    }

    if (
      !selectedPlacements.every(
        (placement) =>
          placement.compatibility === selectedPlacements[0].compatibility
      )
    ) {
      return false;
    }

    if (
      !sqlSelectedPlacements.every(
        (sqlPlacement) =>
          sqlPlacement.Serving_Type === sqlSelectedPlacements[0].Serving_Type
      )
    ) {
      return false;
    }

    let width = selectedPlacements[0].size.width;
    let height = selectedPlacements[0].size.height;

    for (let i = 0; i < selectedPlacements.length; i++) {
      if (
        selectedPlacements[i].size.width !== width ||
        selectedPlacements[i].size.height !== height
      ) {
        return false;
      }
    }

    return true;
  };

  return (
    <div className={classes.root}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={6}>
          <CampaignSelect />
        </Grid>
        <Grid item xs={6}>
          <EnhancedToolbarActions
            onSearch={(e) => setSearchText(e.target.value)}
            onClear={() => setSearchText("")}
            searchTerm={searchText}
            setFilters={setFilters}
            filters={filters}
            tableName="Placements"
          />
        </Grid>
      </Grid>
      <Grid classes={{ container: classes.defaultGrid }} container>
        <Grid item xs={6}>
          <DimensionDefaultList />
        </Grid>
        <Grid item xs={6}>
          <FilterChips
            filters={filters}
            setFilters={setFilters}
            placementSites={placementSites}
          />
        </Grid>
      </Grid>
      {selectedCampaignId !== "" ? (
        <Paper className={classes.paper}>
          <TableContainer className={classes.container}>
            <Table
              className={classes.table}
              size="small"
              aria-labelledby="tableTitle"
              stickyHeader
            >
              <EnhancedTableHead
                classes={classes}
                numSelected={selectedPlacements.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={tableData.length}
              />
              {placements.isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <CircularProgress
                        style={{
                          position: "relative",
                          left: "46.5vw",
                          margin: "6vh 0 2vh 0"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {stableSort(tableData, getSorting(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      let placementInfoObj = placementInfo.byId[row.id]
                        ? placementInfo.byId[row.id]
                        : {};
                      const isItemSelected = isSelected(row);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      const startDate = new Date(placementInfoObj.Start_Date);
                      const now = new Date();
                      const diff = startDate - now;
                      const oneDay = 1000 * 60 * 60 * 24;
                      const day = Math.floor(diff / oneDay);

                      function changeFormat(input) {
                        if (!isNaN(input)) input = parseInt(input);

                        const date = new Date(input);

                        return [
                          ("0" + (date.getUTCMonth() + 1)).slice(-2),
                          ("0" + date.getUTCDate()).slice(-2),
                          ("0" + date.getUTCFullYear()).slice(-2)
                        ].join("/");
                      }

                      function sentenceCase(input) {
                        let word =
                          input.slice(0, 1) + input.slice(1).toLowerCase();
                        return word.replace("_", "").replace("_", " ");
                      }

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.name}
                          selected={isItemSelected}
                        >
                          <TableCell
                            padding="checkbox"
                            className={classes.tableCell}
                          >
                            <Checkbox
                              checked={isItemSelected}
                              onClick={(event) => handleClick(event, row)}
                              inputProps={{ "aria-labelledby": labelId }}
                            />
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {determineStatusColor(placementInfo.byId[row.id])}
                          </TableCell>
                          <TableCell align="left" className={classes.tableCell}>
                            <IconButton
                              onClick={(e) => handleClickOpen(row.id)}
                              disabled={areCreativesLoaded}
                            >
                              {!areCreativesLoaded ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell align="left" className={classes.tableCell}>
                            {placementSites[row.siteId]
                              ? placementSites[row.siteId].name
                              : "Loading Site Name"}
                          </TableCell>
                          <TableCell align="left" className={classes.tableCell}>
                            <div
                              style={{
                                wordBreak: "break-all"
                              }}
                            >
                              {row.name}
                            </div>
                          </TableCell>
                          <TableCell
                            id={labelId}
                            align="left"
                            className={classes.tableCell}
                          >
                            {row.id}
                          </TableCell>
                          <TableCell
                            align="left"
                            title="Date Format: MM/DD/YY"
                            className={classes.tableCell}
                          >
                            {placementInfo.byId[row.id]
                              ? changeFormat(
                                  placementInfo.byId[row.id].Start_Date
                                )
                              : "Loading..."}
                          </TableCell>
                          <TableCell
                            align="left"
                            title="Date Format: MM/DD/YY"
                            className={classes.tableCell}
                          >
                            {placementInfo.byId[row.id]
                              ? changeFormat(
                                  placementInfo.byId[row.id].End_Date
                                )
                              : "Loading..."}
                          </TableCell>
                          <TableCell
                            align="left"
                            className={
                              day < 0 ? classes.overdue : classes.tableCell
                            }
                          >
                            {day.toString()}
                          </TableCell>
                          <TableCell
                            align="left"
                            title="Date Format: MM/DD/YY"
                            className={classes.tableCell}
                          >
                            {row.lastModifiedInfo.time
                              ? changeFormat(row.lastModifiedInfo.time)
                              : "Loading..."}
                          </TableCell>
                          <TableCell align="left" className={classes.tableCell}>
                            {row.size.width + " x " + row.size.height}
                          </TableCell>
                          <TableCell align="left" className={classes.tableCell}>
                            {sentenceCase(row.compatibility)}
                          </TableCell>
                          <TableCell align="left" className={classes.tableCell}>
                            {determineServingType(placementInfo.byId[row.id])}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 0 * emptyRows }}></TableRow>
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="center"
          >
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                "aria-label": "previous page"
              }}
              nextIconButtonProps={{
                "aria-label": "next page"
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              className={classes.pagination}
            />
            <EnhancedTableToolbar
              selected={selectedPlacements}
              isCampaignSelected={Boolean(selectedCampaignId !== "")}
              numSelected={selectedPlacements.length}
              isAddingCreativeAllowed={isAddingCreativeAllowed()}
              setFilters={setFilters}
              filters={filters}
              placementSites={placementSites}
            />
          </Grid>
        </Paper>
      ) : null}

      <AssignedCreativesDialog
        open={open}
        setOpen={setOpen}
        placementId={creativesModalState.selectedId}
        setCreativePreviewURL={setCreativePreviewURL}
        setSelectedCreativePreview={setSelectedCreativePreview}
        setPreviewDialogOpen={setPreviewDialogOpen}
      />

      <CreativePreviewDialog
        setOpen={setOpen}
        previewDialogOpen={previewDialogOpen}
        setPreviewDialogOpen={setPreviewDialogOpen}
        creativePreviewURL={creativePreviewURL}
        selectedCreativePreview={selectedCreativePreview}
        previewType={selectedCreativePreview?.previewType}
        creativeName={selectedCreativePreview?.creativeName}
        creativeId={selectedCreativePreview?.creativeId}
        creativeType={selectedCreativePreview?.creativeType}
      />
    </div>
  );
}
